import { ViewConfig } from '../../../models/view-config.interface';
import { EventsPaginatedService } from '@app/shared/services/events-paginated.service';
import { getCurrentOrganizationSiteKey } from '@app/stores/appStore';
import { ConsigneeViewActionsService } from '../consignee-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { ColumnCreator, ColumnValue } from '../column-creator';
import { NotificationActionsComponent } from '@app/consignee/components/notifications/notification-actions.component';
import {
    NotificationDeliveryNote,
    TableRowData,
} from '@app/shared/models/table-data-types';
import {
    Event,
    SelfCheckinRegistrationCompletedEventPayload,
    SelfCheckinDriverWaitingEventPayload,
    SelfCheckinCheckinFinishedEventPayload,
    DeliveryNoteBundleEvents,
    DeliveryNoteEvents,
} from 'api/models';
import { getEvents } from '@app/stores/eventsStore';

// Type guard to check if event payload has deliveryNoteBundles
function hasDeliveryNoteBundles(
    payload: unknown
): payload is
    | SelfCheckinRegistrationCompletedEventPayload
    | SelfCheckinDriverWaitingEventPayload
    | SelfCheckinCheckinFinishedEventPayload {
    if (!payload || typeof payload !== 'object') {
        return false;
    }

    if (!('deliveryNoteBundles' in payload)) {
        return false;
    }

    const typedPayload = payload as { deliveryNoteBundles: unknown };
    return Array.isArray(typedPayload.deliveryNoteBundles);
}

export interface ConsigneeNotificationsConfigDeps {
    eventsPaginatedService: EventsPaginatedService;
    actionsService: ConsigneeViewActionsService;
}

export class ConsigneeNotificationsConfig {
    static create(deps: ConsigneeNotificationsConfigDeps): ViewConfig {
        return {
            id: 'consignee-notifications',
            name: 'Notifications',
            translationKey: 'OUTGOING-GOODS.TOGGLER.NOTIFICATIONS',
            type: 'notifications',
            userRole: 'consignee',
            apiConfig: {
                loadData: () => {
                    return deps.eventsPaginatedService.loadEventsPaginated();
                },
            },
            tableConfig: {
                columns: [
                    { ...ColumnCreator.details(), sortable: false },
                    { ...ColumnCreator.dateCreated({ showTime: true }), sortable: false },
                    { ...ColumnCreator.notificationStatus(), sortable: false },
                    {
                        ...ColumnCreator.notificationBundleNumber(),
                        sortable: false,
                    },
                    {
                        ...ColumnCreator.notificationDriverName(),
                        sortable: false,
                    },
                    {
                        ...ColumnCreator.notificationDriverNumberPlate(),
                        sortable: false,
                    },
                    {
                        ...ColumnCreator.notificationBookingNumber(),
                        sortable: false,
                    },
                    {
                        ...ColumnCreator.notificationPagerNumber(),
                        sortable: false,
                    },
                    {
                        ...ColumnCreator.notificationPhoneNumber(),
                        sortable: false,
                    },
                    {
                        ...ColumnCreator.notificationReferenceNumber(),
                        sortable: false,
                    },
                    {
                        ...ColumnCreator.notificationTimeSlotBookingNumber(),
                        sortable: false,
                    },
                    {
                        ...ColumnCreator.notificationWaitingNumber(),
                        sortable: false,
                    },
                    { ...ColumnCreator.organizationSiteKey(), sortable: false },
                    ColumnCreator.actions({
                        width: '150px',
                        configuration: {
                            type: 'notification',
                            showGoToProcess: true,

                            // Endpoints
                            goToProcessEndpoint: (item: Event) =>
                                deps.actionsService.openCheckInPage(item),

                            // Configuration
                            organizationSiteKey:
                                getCurrentOrganizationSiteKey(),
                            context: 'consignee',

                            // Visibility conditions
                            isGoToProcessVisible: (item: Event) => {
                                const payload = item.payload;
                                if (!payload) return false;

                                // Hide button for finished checkin events
                                if (
                                    payload.eventType ===
                                    'self_checkin_checkin_finished'
                                ) {
                                    return false;
                                }

                                // Show for registration and waiting events that have checkinKey
                                if (
                                    (payload.eventType ===
                                        'self_checkin_registration_completed' ||
                                        payload.eventType ===
                                            'self_checkin_driver_waiting') &&
                                    'checkinKey' in payload
                                ) {
                                    // Check if there's a finished event with the same checkinKey
                                    const eventsArray = getEvents();
                                    const checkinKey = payload.checkinKey;

                                    const hasFinishedEvent = eventsArray.some(
                                        e =>
                                            e.payload?.eventType ===
                                                'self_checkin_checkin_finished' &&
                                            'checkinKey' in e.payload &&
                                            e.payload.checkinKey === checkinKey
                                    );

                                    // Hide button if process is finished
                                    return !hasFinishedEvent;
                                }

                                return false;
                            },
                        } as TableActionsConfig,
                    }),
                ],
                features: {
                    sortable: false,
                    filterable: true,
                    paginated: true,
                    selectable: false,
                    expandable: true,
                },
                expandedRowConfig: {
                    expandableCondition: (item: Event) => {
                        if (!hasDeliveryNoteBundles(item.payload)) return false;
                        return item.payload.deliveryNoteBundles.length > 0;
                    },
                    tableConfig: {
                        columns: [
                            {
                                key: 'notificationActions',
                                header: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.ACTIONS',
                                width: '150px',
                                sortable: false,
                                filterable: false,
                                component: NotificationActionsComponent,
                                componentInputs: (
                                    row: TableRowData,
                                    value: ColumnValue,
                                    parentRow?: Event
                                ) => ({
                                    row: row,
                                    parentRow: parentRow,
                                }),
                            },
                            ColumnCreator.custom({
                                key: 'bundleNumber',
                                header: 'OUTGOING-GOODS.COLUMN-FILTERS.bundleKey',
                                sortable: false,
                                filterable: false,
                            }),
                            ColumnCreator.deliveryNoteNumber({
                                header: 'INCOMING-GOODS.TABLE.DELIVERY-NO',
                                sortable: false,
                                filterable: false,
                            }),
                        ],
                        apiConfig: {
                            loadData: async (
                                parentRow: Event,
                                organizationSiteKey: string,
                                customParams?: Record<string, unknown>
                            ) => {
                                if (hasDeliveryNoteBundles(parentRow.payload)) {
                                    const allDeliveryNotes: NotificationDeliveryNote[] =
                                        [];

                                    parentRow.payload.deliveryNoteBundles.forEach(
                                        (bundle: DeliveryNoteBundleEvents) => {
                                            if (bundle.deliveryNotes) {
                                                bundle.deliveryNotes.forEach(
                                                    (
                                                        deliveryNote: DeliveryNoteEvents
                                                    ) => {
                                                        allDeliveryNotes.push({
                                                            ...deliveryNote,
                                                            bundleNumber:
                                                                bundle.bundleNumber,
                                                            bundleKey:
                                                                bundle.key, // Add bundle key for actions
                                                            parentEventKey:
                                                                parentRow.eventKey,
                                                            parentOrgSiteKey:
                                                                parentRow.organizationSiteKey,
                                                        });
                                                    }
                                                );
                                            }
                                        }
                                    );

                                    return allDeliveryNotes;
                                }
                                return [];
                            },
                            organizationSiteKeyResolver: (
                                row: Event | NotificationDeliveryNote
                            ) =>
                                'parentOrgSiteKey' in row
                                    ? row.parentOrgSiteKey
                                    : row.organizationSiteKey,
                            customParams: (
                                row: Event | NotificationDeliveryNote
                            ) => ({
                                eventKey:
                                    'parentEventKey' in row
                                        ? row.parentEventKey
                                        : row.eventKey,
                                bundleNumber:
                                    'bundleNumber' in row
                                        ? row.bundleNumber
                                        : '',
                            }),
                        },
                        loadingMessage: 'GENERAL.LOADING',
                        emptyMessage: 'GENERAL.NO-DATA',
                        tableClass: 'expanded-notification-details-table',
                        maxHeight: '300px',
                        gridConfig: {
                            containerClass: 'tw-grid tw-grid-cols-12',
                            headerClass: 'tw-col-span-12',
                            cellClasses: {
                                bundleNumber: 'tw-col-span-1',
                                actions: 'tw-col-span-1',
                            },
                        },
                    },
                },
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'eventType',
                        label: 'OUTGOING-GOODS.NOTIFICATION-VIEW.TABLE-HEADER.EVENT',
                        translationKey:
                            'OUTGOING-GOODS.NOTIFICATION-VIEW.TABLE-HEADER.EVENT',
                    },
                    {
                        keyName: 'bundleNr',
                        label: 'OUTGOING-GOODS.COLUMN-FILTERS.bundleKey',
                        translationKey:
                            'OUTGOING-GOODS.COLUMN-FILTERS.bundleKey',
                    },
                    {
                        keyName: 'organizationSiteKey',
                        label: 'OUTGOING-GOODS.NOTIFICATION-VIEW.TABLE-HEADER.LOCATION',
                        translationKey:
                            'OUTGOING-GOODS.NOTIFICATION-VIEW.TABLE-HEADER.LOCATION',
                    },
                ],
                showClosed: true,
                enableAdvancedFilters: true,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'eventType',
                    'bundleNr',
                    'organizationSiteKey',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: false,
                canUpdate: false,
                canDelete: false,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
        };
    }
}
